/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const WorkingDetailsModal = ({
  caregiverId,
  workingDetails,
  loading,
  onClose,
}) => {
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(loading);

  // Update details when props change
  useEffect(() => {
    if (workingDetails && workingDetails.success) {
      const data = workingDetails.data;

      if (data.activeBooking) {
        setDetails({
          success: true,
          workingType: "appointment",
          workingData: {
            _id: data.activeBooking.id,
            status: data.activeBooking.status,
            slotDate: data.activeBooking.slotDate,
            slotTime: data.activeBooking.slotTime,
            amount: data.activeBooking.agreedAmount,
            location: data.activeBooking.location,
            additionalNotes: data.activeBooking.additionalNotes,
            created_by: data.activeBooking.user,
            createdAt: data.activeBooking.createdAt,
          },
        });
      } else if (data.activeJob) {
        setDetails({
          success: true,
          workingType: "job",
          workingData: {
            _id: data.activeJob.id,
            workState: data.activeJob.workState,
            workTime: data.activeJob.workTime,
            category: data.activeJob.serviceCategory?.label,
            salary: data.activeJob.requirementSnapshot?.salary,
            workDays: data.activeJob.requirementSnapshot?.workDays,
            workHours: data.activeJob.requirementSnapshot?.workHours,
            location: data.activeJob.location,
            subcity: data.activeJob.subcity,
            description: data.activeJob.description,
            created_by: data.activeJob.createdBy,
            createdAt: data.activeJob.createdAt,
            startDate: data.activeJob.startedAt,
          },
        });
      } else {
        setDetails({ success: false });
      }
    } else {
      setDetails(workingDetails);
    }
    setIsLoading(loading);
  }, [workingDetails, loading]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!details) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusConfig = (status) => {
    const statusLower = status?.toLowerCase();

    if (
      statusLower?.includes("active") ||
      statusLower?.includes("completed") ||
      statusLower?.includes("accepted") ||
      statusLower?.includes("paid")
    ) {
      return {
        color: "emerald",
        bg: "bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 dark:from-emerald-900/30 dark:to-emerald-800/30",
        text: "text-emerald-700 dark:text-emerald-300",
        border: "border-emerald-200 dark:border-emerald-800",
        gradient: "from-emerald-500 to-teal-500",
      };
    }

    if (
      statusLower?.includes("pending") ||
      statusLower?.includes("waiting") ||
      statusLower?.includes("scheduled")
    ) {
      return {
        color: "amber",
        bg: "bg-gradient-to-r from-amber-500/10 to-orange-500/10 dark:from-amber-900/30 dark:to-orange-800/30",
        text: "text-amber-700 dark:text-amber-300",
        border: "border-amber-200 dark:border-amber-800",
        gradient: "from-amber-500 to-orange-500",
      };
    }

    if (
      statusLower?.includes("cancelled") ||
      statusLower?.includes("rejected") ||
      statusLower?.includes("failed")
    ) {
      return {
        color: "rose",
        bg: "bg-gradient-to-r from-rose-500/10 to-pink-500/10 dark:from-rose-900/30 dark:to-pink-800/30",
        text: "text-rose-700 dark:text-rose-300",
        border: "border-rose-200 dark:border-rose-800",
        gradient: "from-rose-500 to-pink-500",
      };
    }

    return {
      color: "gray",
      bg: "bg-gradient-to-r from-gray-500/10 to-gray-600/10 dark:from-gray-800/30 dark:to-gray-700/30",
      text: "text-gray-700 dark:text-gray-300",
      border: "border-gray-200 dark:border-gray-700",
      gradient: "from-gray-500 to-gray-600",
    };
  };

  const getWorkTypeConfig = (type) => {
    if (type === "appointment") {
      return {
        bg: "bg-gradient-to-r from-primary/15 to-pink-500/15 dark:from-primary/20 dark:to-pink-500/20",
        border: "border-primary/20 dark:border-primary/30",
        color: "text-primary dark:text-primary",
        label: "One-time Appointment",
        gradient: "from-primary to-pink-500",
      };
    }
    return {
      bg: "bg-gradient-to-r from-secondary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-500/20",
      border: "border-secondary/20 dark:border-primary/30",
      color: "text-secondary dark:text-primary",
      label: "Job Contract",
      gradient: "from-secondary to-blue-500 dark:from-primary to-blue-400",
    };
  };

  const renderClientCard = (clientData) => {
    if (!clientData) return null;

    return (
      <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h4 className="text-lg font-bold text-text dark:text-dark-text mb-1">
              {clientData.name || "Unknown Client"}
            </h4>
            <p className="text-sm text-gray-500 dark:text-dark-muted">
              {details.workingType === "appointment"
                ? "Appointment Client"
                : "Job Employer"}
            </p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-500 dark:text-dark-muted mb-1">
              Phone
            </p>
            <p className="text-text dark:text-dark-text font-medium">
              {clientData.phoneNumber || "N/A"}
            </p>
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <p className="text-xs text-gray-500 dark:text-dark-muted mb-1">
              Email
            </p>
            <p className="text-text dark:text-dark-text font-medium truncate">
              {clientData.email || "N/A"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderAppointmentDetails = () => {
    const data = details.workingData;
    const statusConfig = getStatusConfig(data.status);
    const workConfig = getWorkTypeConfig("appointment");

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h4 className="text-xl font-bold text-text dark:text-dark-text">
              Appointment Details
            </h4>
            <p className="text-sm text-gray-500 dark:text-dark-muted">
              ID: {data.id?.substring(0, 12)}...
            </p>
          </div>

          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig.bg} ${statusConfig.border} border`}
          >
            <span className={`font-bold ${statusConfig.text}`}>
              {data.status || "N/A"}
            </span>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Schedule */}
          <div className="space-y-6">
            <div className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface">
              <h5 className="font-bold text-text dark:text-dark-text mb-4">
                Schedule
              </h5>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <span className="text-gray-600 dark:text-dark-muted">
                    Date
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {data.slotDate || "N/A"}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <span className="text-gray-600 dark:text-dark-muted">
                    Time
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {data.slotTime || "N/A"}
                  </span>
                </div>

                {data.duration && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <span className="text-gray-600 dark:text-dark-muted">
                      Duration
                    </span>
                    <span className="font-bold text-text dark:text-dark-text">
                      {data.duration} hours
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Card */}
            <div className="rounded-2xl p-6 border border-primary/20 dark:border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5">
              <h5 className="font-bold text-text dark:text-dark-text mb-4">
                Wage
              </h5>

              <div className="text-center">
                <div className="text-3xl font-bold text-primary dark:text-primary mb-1">
                  Br {data.amount || "0.00"}
                </div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">
                  Total Amount
                </p>
              </div>
            </div>
          </div>

          {/* Middle Column - Client Card */}
          <div className="lg:col-span-2">
            {renderClientCard(data.created_by)}

            {/* Location & Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {data.location && (
                <div className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface">
                  <h5 className="font-bold text-text dark:text-dark-text mb-4">
                    Location
                  </h5>
                  <p className="text-text dark:text-dark-text font-medium">
                    {data.location}
                  </p>
                </div>
              )}

              {data.additionalNotes && (
                <div className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface">
                  <h5 className="font-bold text-text dark:text-dark-text mb-4">
                    Notes
                  </h5>
                  <div className="max-h-32 overflow-y-auto">
                    <p className="text-text dark:text-dark-text text-sm whitespace-pre-line">
                      {data.additionalNotes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderJobDetails = () => {
    const data = details.workingData;
    const statusConfig = getStatusConfig(data.workState);
    const workConfig = getWorkTypeConfig("job");

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h4 className="text-xl font-bold text-text dark:text-dark-text">
              Job Contract
            </h4>
            <p className="text-sm text-gray-500 dark:text-dark-muted">
              Contract ID: {data.id?.substring(0, 12)}...
            </p>
          </div>

          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig.bg} ${statusConfig.border} border`}
          >
            <span className={`font-bold ${statusConfig.text}`}>
              {data.workState || "N/A"}
            </span>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Job Info */}
          <div className="space-y-6">
            <div className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface">
              <h5 className="font-bold text-text dark:text-dark-text mb-4">
                Job Details
              </h5>

              <div className="space-y-4">
                {data.workTime && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <span className="text-gray-600 dark:text-dark-muted">
                      Work Time
                    </span>
                    <span className="font-bold text-text dark:text-dark-text capitalize">
                      {data.workTime.replace(/_/g, " ")}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <span className="text-gray-600 dark:text-dark-muted">
                    Category
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {data.category || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Salary Card */}
            {data.salary && (
              <div className="rounded-2xl p-6 border border-primary/20 dark:border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5">
                <h5 className="font-bold text-text dark:text-dark-text mb-4">
                  Monthly Wage
                </h5>

                <div className="text-center">
                  <div className="text-3xl font-bold text-primary dark:text-primary mb-2">
                    Br {data.salary}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-dark-muted">
                    Per Month
                  </p>

                  {(data.workDays || data.workHours) && (
                    <div className="mt-4 pt-4 border-t border-primary/20 dark:border-primary/30">
                      <div className="flex items-center justify-center gap-4">
                        {data.workDays && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-dark-muted">
                              Days
                            </p>
                            <p className="font-bold text-text dark:text-dark-text">
                              {data.workDays}
                            </p>
                          </div>
                        )}
                        {data.workHours && (
                          <div>
                            <p className="text-xs text-gray-500 dark:text-dark-muted">
                              Hours
                            </p>
                            <p className="font-bold text-text dark:text-dark-text">
                              {data.workHours}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Middle & Right Columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Card */}
            {renderClientCard(data.created_by)}

            {/* Location & Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.location && (
                <div className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface">
                  <h5 className="font-bold text-text dark:text-dark-text mb-4">
                    Location
                  </h5>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-dark-muted mb-1">
                        Address
                      </p>
                      <p className="font-medium text-text dark:text-dark-text">
                        {data.location}
                      </p>
                    </div>

                    {data.subcity && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-dark-muted">
                          Subcity
                        </span>
                        <span className="font-bold text-text dark:text-dark-text">
                          {data.subcity}
                        </span>
                      </div>
                    )}

                    {data.address && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-text dark:text-dark-text">
                          {data.address}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {data.description && (
                <div className="rounded-2xl p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface">
                  <h5 className="font-bold text-text dark:text-dark-text mb-4">
                    Description
                  </h5>

                  <div className="max-h-48 overflow-y-auto">
                    <p className="text-text dark:text-dark-text text-sm whitespace-pre-line leading-relaxed">
                      {data.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-dark-muted">
            <div className="flex items-center gap-4">
              <span>Created {formatDate(data.createdAt)}</span>
              {data.startDate && (
                <>
                  <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                  <span>Starts {formatDate(data.startDate)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNoWorkingDetails = () => (
    <div className="text-center py-12 px-4">
      <h3 className="text-2xl font-bold text-text dark:text-dark-text mb-3">
        No Active Assignments
      </h3>
      <p className="text-gray-500 dark:text-dark-muted max-w-md mx-auto mb-8 leading-relaxed">
        This caregiver is currently available and not assigned to any
        appointments or job contracts.
      </p>

      <div className="inline-flex flex-col items-center gap-4">
        <div className="px-4 py-2 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full">
          <span className="font-medium">Available for new assignments</span>
        </div>

        <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-dark-muted">
          <span>Ready to work</span>
          <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
          <span>Open for opportunities</span>
        </div>
      </div>
    </div>
  );

  const workConfig = getWorkTypeConfig(details?.workingType || "appointment");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden animate-in fade-in zoom-in-95 duration-300 max-h-[95vh] flex flex-col border border-gray-200 dark:border-dark-border">
        {/* Header */}
        <div className="relative px-8 py-6">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-pink-500"></div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-text dark:text-dark-text">
                Working Details
              </h3>
              <p className="text-sm text-gray-500 dark:text-dark-muted mt-1 flex items-center gap-2">
                <span>{workConfig.label}</span>
                <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <span>Caregiver ID: {caregiverId?.substring(0, 8)}...</span>
              </p>
            </div>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all"
              aria-label="Close modal"
            >
              <X size={20} className="text-gray-500 dark:text-dark-muted" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="px-8 py-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
              <p className="text-lg font-medium text-text dark:text-dark-text mt-6 mb-2">
                Loading Details
              </p>
              <p className="text-gray-500 dark:text-dark-muted">
                Fetching the latest information...
              </p>
            </div>
          ) : details && details.success && details.workingType ? (
            <div className="space-y-8">
              {details.workingType === "appointment" &&
                renderAppointmentDetails()}
              {details.workingType === "job" && renderJobDetails()}
            </div>
          ) : (
            renderNoWorkingDetails()
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-200 dark:border-dark-border">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingDetailsModal;
